import {
    AssetDTO,
    DialogContentDTO,
    EncountersDTO,
    EnemyDTO,
    InventoryItemDTO,
    NPCDTO,
    PointDTO,
    SceneDTO,
    SpriteDataDTO,
    TiledMapDTO,
    WarpPointDTO,
} from "./dtos";
import { DialogEvent, Direction, Endpoints, getAssetUrl, Tiled } from "./enum";

export class Controls {
    actionKey: string;
    cancelKey: string;
    menuKey: string;
}

export class Point {
    constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

    x: number;
    y: number;

    public static fromDTO(dto: PointDTO): Point {
        return new Point(dto.x, dto.y);
    }
}

export interface SceneParams {}

// à terme: sortir la dépendance à Phaser.Scene pour n'utiliser que des IDs
export class DialogSceneParams implements SceneParams {
    public scene: Phaser.Scene;
    public dialog: DialogContentDTO;
    public triggeringNpc: string;
}

export class BattleParams implements SceneParams {
    public scene: Phaser.Scene;
}

export class BattleContextParams implements SceneParams {
    public origin: string;
    public enemies: EnemyDTO[];
    public assets: Asset[];
}

export class MenuSceneParams implements SceneParams {
    public scene: Phaser.Scene;
    public mode: string;
    public item: InventoryItemDTO;
}

export class Surface {
    constructor(x: number, y: number, width: number, height: number) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
    }

    x: number;
    y: number;
    width: number;
    height: number;
}

export class Warp {
    constructor(targetSceneId: string, targetLabelName: string, surface: Surface) {
        this.targetSceneId = targetSceneId;
        this.targetLabelName = targetLabelName;
        this.surface = surface;
    }

    public targetSceneId: string;
    public targetLabelName: string;
    public surface: Surface;

    public centerX() {
        return this.surface.x + this.surface.width / 2;
    }

    public centerY() {
        return this.surface.y + this.surface.height / 2;
    }

    public width() {
        return this.surface.width;
    }
    public height() {
        return this.surface.height;
    }
}

export class WarpPoint {
    constructor(
        id: string,
        name: string,
        location: Point,
        isDefault: boolean,
        targetSceneId: string | null,
        targetWarpId: string | null,
        conditions: string[],
        metadata: Record<string, any> | null
    ) {
        this.id = id;
        this.name = name;
        this.location = location;
        this.isDefault = isDefault;
        this.targetSceneId = targetSceneId;
        this.targetWarpId = targetWarpId;
        this.conditions = conditions;
        this.metadata = metadata;
    }

    public id: string;
    public name: string;
    public location: Point;
    public isDefault: boolean;
    public targetSceneId: string | null;
    public targetWarpId: string | null;
    public conditions: string[];
    public metadata: Record<string, any> | null;

    public static fromDTO(dto: WarpPointDTO): WarpPoint {
        return new WarpPoint(
            dto.id,
            dto.name,
            Point.fromDTO(dto.location),
            dto.is_default,
            dto.target_scene_id,
            dto.target_warp_id,
            dto.conditions || [],
            dto.metadata
        );
    }

    public centerX(): number {
        const width = this.metadata?.width || 32;
        return this.location.x + width / 2;
    }

    public centerY(): number {
        const height = this.metadata?.height || 32;
        return this.location.y + height / 2;
    }

    public width(): number {
        return this.metadata?.width || 32;
    }

    public height(): number {
        return this.metadata?.height || 32;
    }
}

export class NpcSprite {
    constructor(id: string, x: number, y: number, spriteData: SpriteData) {
        this.id = id;
        this.x = x;
        this.y = y;
        this.spriteData = spriteData;
    }

    public id: string;
    public x: number;
    public y: number;
    public spriteData: SpriteData;
}

export class PlayerSprite {
    public playerDirection: Direction;
    public sprite: Phaser.Physics.Arcade.Sprite;

    constructor(sprite: Phaser.Physics.Arcade.Sprite) {
        this.sprite = sprite;
        this.playerDirection = Direction.DOWN; // Default facing down
    }

    // TODO: move the playersprite to some internal property
    public updateFromInput(cursors: Phaser.Types.Input.Keyboard.CursorKeys, playerSprite: any) {
        playerSprite.body.setVelocity(0);

        // Horizontal movement
        if (cursors.left.isDown) {
            //@ts-ignore
            playerSprite.body.setVelocityX(-80);
        } else if (cursors.right.isDown) {
            //@ts-ignore
            playerSprite.body.setVelocityX(80);
        }
        // Vertical movement
        if (cursors.up.isDown) {
            //@ts-ignore
            playerSprite.body.setVelocityY(-80);
        } else if (cursors.down.isDown) {
            //@ts-ignore
            playerSprite.body.setVelocityY(80);
        }

        // Update direction and animation - give left/right precedence over up/down
        if (cursors.left.isDown) {
            this.playerDirection = Direction.LEFT;
            playerSprite.anims.play("left", true);
            //            playerSprite.flipX = true;
        } else if (cursors.right.isDown) {
            this.playerDirection = Direction.RIGHT;
            playerSprite.anims.play("right", true);
            //            playerSprite.flipX = false;
        } else if (cursors.up.isDown) {
            this.playerDirection = Direction.UP;
            playerSprite.anims.play("up", true);
        } else if (cursors.down.isDown) {
            this.playerDirection = Direction.DOWN;
            playerSprite.anims.play("down", true);
        } else {
            playerSprite.anims.stop();
        }
    }

    /**
     * Get the facing direction as lowercase string for backend communication
     * @returns "up" | "down" | "left" | "right"
     */
    public getFacingDirection(): string {
        switch (this.playerDirection) {
            case Direction.UP:
                return "up";
            case Direction.DOWN:
                return "down";
            case Direction.LEFT:
                return "left";
            case Direction.RIGHT:
                return "right";
            default:
                return "down";
        }
    }
}

export class Scene {
    constructor(
        id: string,
        tiledMap: TiledMapDTO,
        npcs: NPC[],
        encounters: Encounters,
        assets: Asset[],
        warpPoints: WarpPoint[]
    ) {
        this.id = id;
        this.tiledMap = tiledMap;
        this.npcs = npcs;
        this.encounters = encounters;
        this.assets = assets;
        this.warpPoints = warpPoints;
    }

    id: string;
    tiledMap: TiledMapDTO; // debt: convert to TiledMap
    npcs: NPC[];
    encounters: Encounters;
    assets: Asset[];
    warpPoints: WarpPoint[];

    public static fromDTO(scene: SceneDTO): Scene {
        return new Scene(
            scene.id,
            scene.tiled_map,
            scene.npcs?.map((npc) => NPC.fromDTO(npc)),
            Encounters.fromDTO(scene.encounters),
            scene.assets.map((asset) => Asset.fromDTO(asset)),
            scene.warp_points?.map((warp) => WarpPoint.fromDTO(warp)) || []
        );
    }

    public getSpriteesheetsAsURL(): any {
        const urls = [];
        console.log("loading spritesheets from assets: ", this.assets);
        this.assets.forEach((asset) => {
            if (
                asset.category === "spritesheet" ||
                asset.category === "tileset" ||
                asset.category === "characters" ||
                asset.category === "items"
            ) {
                const width = asset.spriteWidth ? asset.spriteWidth : 32;
                const height = asset.spriteHeight ? asset.spriteHeight : 32;

                const url = {
                    key: asset.category + "/" + asset.filename,
                    url: asset.onlineImage ? getAssetUrl(asset.onlineImage) : "",
                    frameConfig: { frameWidth: width, frameHeight: height },
                };
                console.log("adding spritesheet url: ", url);
                urls.push(url);
            }
        });

        return urls;
    }
}

export class Encounters {
    constructor(common: string[], uncommon: string[], rare: string[], veryRare: string[]) {
        this.common = common;
        this.uncommon = uncommon;
        this.rare = rare;
        this.veryRare = veryRare;
    }

    common: string[];
    uncommon: string[];
    rare: string[];
    veryRare: string[];

    public static fromDTO(encounters: EncountersDTO): Encounters {
        if (encounters) {
            return new Encounters(
                encounters.common,
                encounters.uncommon,
                encounters.rare,
                encounters.very_rare
            );
        }
    }
}

export class Character {
    constructor(
        characterId: string,
        name: string,
        spriteData: SpriteData,
        frameIndex: number,
        type: string
    ) {
        this.characterId = characterId;
        this.name = name;
        this.spriteData = spriteData;
        this.frameIndex = frameIndex;
        this.type = type;
    }

    characterId: string;
    name: string;
    spriteData: SpriteData;
    frameIndex: number;
    type: string;
}

export class NPC extends Character {
    dialogEvent: DialogEvent;
    position?: Point;

    constructor(
        characterId: string,
        name: string,
        spriteData: SpriteData,
        frameIndex: number,
        type: string,
        dialogEvent: DialogEvent,
        position?: Point
    ) {
        super(characterId, name, spriteData, frameIndex, type);
        this.dialogEvent = dialogEvent;
        this.position = position;
    }

    public static fromDTO(npc: NPCDTO): NPC {
        return new NPC(
            npc.character_id,
            npc.name,
            SpriteData.fromDTO(npc.sprite_data),
            npc.frame_index,
            npc.type,
            npc.dialog_event,
            npc.position ? Point.fromDTO(npc.position) : undefined
        );
    }
}

export class SpriteData {
    constructor(
        imagePath: string,
        type: string,
        frameName: string,
        battleFrameName: number,
        width?: number,
        height?: number
    ) {
        this.imagePath = imagePath;
        this.type = type;
        this.frameName = frameName;
        this.battleFrameName = battleFrameName;
        this.width = width;
        this.height = height;
    }

    imagePath: string;
    // type = atlas or spritesheet
    type: string;
    frameName: string;
    battleFrameName: number;
    width: number = 32;
    height: number = 64;

    public static fromDTO(sprite: SpriteDataDTO): SpriteData {
        return new SpriteData(
            sprite.image_path,
            sprite.type,
            sprite.frame_name,
            sprite.battle_frame_name,
            sprite.width,
            sprite.height
        );
    }
}

export class Asset {
    constructor(
        id: string | number,
        category: string,
        type: string,
        filename: string,
        content: Blob,
        path: string,
        spriteWidth: number,
        spriteHeight: number,
        onlineImage?: string
    ) {
        this.id = id;
        this.category = category;
        this.type = type;
        this.filename = filename;
        this.content = content;
        this.path = path;
        this.spriteWidth = spriteWidth;
        this.spriteHeight = spriteHeight;
        this.onlineImage = onlineImage;
    }

    id: string | number;
    category: string;
    type: string;
    filename: string;
    content: Blob;
    path: string;
    spriteWidth: number;
    spriteHeight: number;
    onlineImage?: string;

    public static fromDTO(asset: AssetDTO): Asset {
        return new Asset(
            asset.id,
            asset.category,
            asset.type,
            asset.filename,
            asset.content,
            asset.path,
            asset.sprite_width,
            asset.sprite_height,
            asset.online_image
        );
    }
}

export class GameMap {
    public phaserScene: Phaser.Scene;
    public scene: Scene;
    public encounters: Phaser.Physics.Arcade.Group;
    public map: Phaser.Tilemaps.Tilemap;
    public player: Phaser.Physics.Arcade.Sprite;

    constructor(
        player: Phaser.Physics.Arcade.Sprite,
        map: Phaser.Tilemaps.Tilemap,
        scene: Phaser.Scene,
        sceneModel: Scene
    ) {
        this.player = player;
        this.map = map;
        this.phaserScene = scene;
        this.scene = sceneModel;
    }

    public createEnemySpawnPoints() {
        const props = this.scene.tiledMap.properties;
        const hasRandomEncounters = props?.some(
            (prop) => prop.name === "random_encounters" && prop.value === true
        );
        if (hasRandomEncounters) {
            // Destroy the old group (and its Zone children) before creating new ones.
            // Without this, every call accumulates ghost zones in the physics world.
            if (this.encounters) {
                this.encounters.clear(true, true);
                this.encounters.destroy();
                this.encounters = null;
            }
            this.encounters = this.generateEncounters();
        }
    }

    public generateEncounters(): Phaser.Physics.Arcade.Group {
        // where the enemies will be
        const spawns = this.phaserScene.physics.add.group({ classType: Phaser.GameObjects.Zone });
        for (let i = 0; i < 30; i++) {
            const x = Phaser.Math.RND.between(0, this.phaserScene.physics.world.bounds.width);
            const y = Phaser.Math.RND.between(0, this.phaserScene.physics.world.bounds.height);
            // parameters are x, y, width, height

            //@ts-ignore
            spawns.create(x, y, 20, 20);
        }
        return spawns;
    }

    public findWarps(): Array<WarpPoint> {
        // NEW: Return warp points from Scene object instead of parsing Tiled map
        // Warp points are now defined in the Scene entity on the backend
        return this.scene.warpPoints || [];
    }

    public findNpcsFromTiledMap(): Array<NpcSprite> {
        const found = [];
        const npcs = this.scene.npcs;

        if (!npcs) {
            return [];
        }
        const layer = this.map.getObjectLayer(Tiled.NPC_LAYER);

        if (!layer) {
            return [];
        }
        layer.objects.forEach((object) => {
            if (!object.properties) {
                return;
            }

            // dette à corriger: utilisation de TYPE au lieu d'un vrai ID
            const npcId = object.properties.find(
                (property) => property.name === Tiled.NPC_ID_PROPERTY
            );

            if (!npcId) {
                return;
            }
            const targetNpc = npcs.find((npc) => npc.characterId === npcId.value);

            if (!targetNpc) {
                return;
            }
            console.log(
                "trying to find " + npcId.value + ", target npc: " + targetNpc,
                "npcs: ",
                npcs
            );
            found.push(new NpcSprite(npcId.value, object.x, object.y, targetNpc.spriteData));
            /**
             * !target npc === se produit si le personnage est local à la MAP et n'existe pas dans le référentiel ?
             *
            if (!targetNpc) {
                this.addNpcSprite(npcId.value, object.x, object.y, npcId.value);
            } else {
                this.addNpcSprite(npcId.value, object.x, object.y, targetNpc.sprite_data);
            }*/
        });
        return found;
    }

    public findNpcsFromScene(): Array<NpcSprite> {
        const found = [];
        const npcs = this.scene.npcs;

        if (!npcs) {
            return [];
        }

        npcs.forEach((npc) => {
            if (npc.position && npc.spriteData) {
                found.push(
                    new NpcSprite(npc.characterId, npc.position.x, npc.position.y, npc.spriteData)
                );
            }
        });

        return found;
    }

    public findNpcs(): Array<NpcSprite> {
        const fromTiled = this.findNpcsFromTiledMap();
        const fromScene = this.findNpcsFromScene();

        // Deduplicate: if an NPC exists in both sources, prefer the Tiled version
        // (Tiled positions are considered more authoritative for backward compatibility)
        const tiledNpcIds = new Set(fromTiled.map((npc) => npc.id));
        const uniqueFromScene = fromScene.filter((npc) => !tiledNpcIds.has(npc.id));

        return [...fromTiled, ...uniqueFromScene];
    }

    public loadNPCs() {
        const npcs = this.findNpcs();

        npcs?.forEach((npc: NpcSprite) => {
            this.addNpcSprite(npc.id, npc.x, npc.y, npc.spriteData);
        });
    }

    private addNpcSprite(id: string, x: number, y: number, spriteData: SpriteData) {
        const npc = this.phaserScene.physics.add.sprite(
            x,
            y,
            "characters/" + spriteData.imagePath,
            parseInt(spriteData.frameName)
        );
        npc.setScale(1, 1);
        npc.setSize(spriteData.width, spriteData.height / 2);
        npc.body.offset.y = spriteData.height / 2;
    }
}
